/**
 * iter133 — Kahani Studio Shared Design System.
 *
 * One source of truth for the luxury visual language across every
 * public/client surface: Inquiry, Proposal, Thank-You, and the future
 * Booking Preview + Client Portal. Import from any page and stop
 * duplicating hex codes / cubic-beziers / shadow strings.
 *
 * Feel targets:
 *   • Timeless · Editorial · Minimal · Never flashy.
 *   • Apple / Airbnb Luxe / Aman Resorts pacing — nothing fast, everything
 *     calm. Motion durations start at 500ms; hero animations run 25–30s.
 *   • Line-height and tracking tuned for magazine feel.
 */

// ---------- COLOR PALETTE ----------
export const COLORS = {
  cream: "#F8F4EE",        // Page canvas
  creamDeep: "#F5EFE4",    // Card interior for eyebrow chips / soft fills
  ink: "#3B2F24",          // Primary text
  inkSoft: "#5A4A38",      // Secondary text
  ash: "#7A6853",           // Tertiary text / captions
  muted: "#8C7A63",        // Eyebrow labels, small caps
  tan: "#B6865B",          // Warm brand accent
  tanHover: "#A0754E",     // Hover-darker tan for CTAs
  tanSoft: "#E7D5B4",      // Hairlines, dividers, disabled borders
  paper: "#FFFFFF",
};

// ---------- APPLE-STYLE EASING CURVES ----------
// The "hero" easing lifts objects in with a hint of ease-out. Used for
// hero text, section fade-ups, card entrances. It's the same feel as
// Apple's iOS transitions.
export const EASE = {
  hero: "cubic-bezier(0.32, 0.72, 0, 1)",
  gentle: "cubic-bezier(0.4, 0.0, 0.2, 1)",
  slowOut: "cubic-bezier(0.16, 1, 0.3, 1)",
};

// ---------- DURATIONS ----------
// Everything is slower now. 250ms felt snappy; 500ms feels considered.
export const DUR = {
  hoverSubtle: 350,   // small tint/border changes
  hover: 500,         // lifts, glows
  transition: 700,    // section/card entrances
  hero: 1200,         // hero text staggered reveals
  kenBurns: 30,       // seconds
};

// ---------- SHADOWS ----------
// Soft, warm-tinted. Not grey. Feels like light through parchment.
export const SHADOWS = {
  card:        "0 2px 4px rgba(140,90,50,0.03), 0 12px 32px rgba(140,90,50,0.06)",
  cardHover:   "0 4px 8px rgba(140,90,50,0.05), 0 24px 60px rgba(140,90,50,0.10)",
  floating:    "0 30px 80px rgba(140,90,50,0.10)",
  glowTan:     "0 14px 32px rgba(182,134,91,0.28)",
  glowTanBig:  "0 20px 48px rgba(182,134,91,0.42)",
  hairline:    "0 1px 0 rgba(140,90,50,0.03)",
};

// ---------- RADIUS ----------
export const RADIUS = {
  sm: 12,
  md: 16,
  lg: 20,
  xl: 24,      // "premium stationery" card
  xxl: 28,     // hero card
  pill: 999,
};

// ---------- TYPOGRAPHY ----------
export const FONTS = {
  display: "'Playfair Display', ui-serif, Georgia, serif",
  body: "'IBM Plex Sans', ui-sans-serif, system-ui, sans-serif",
  mono: "'IBM Plex Mono', ui-monospace, monospace",
};

// Reusable text style presets — spread into a `style={...}` to keep
// typographic rhythm consistent across pages.
export const TEXT = {
  heroTitle: {
    fontFamily: FONTS.display,
    fontWeight: 500,
    letterSpacing: "-0.015em",
    lineHeight: 1.02,
  },
  displayLg: {
    fontFamily: FONTS.display,
    fontWeight: 500,
    letterSpacing: "-0.005em",
    lineHeight: 1.15,
  },
  displayMd: {
    fontFamily: FONTS.display,
    fontWeight: 500,
    letterSpacing: "0",
    lineHeight: 1.25,
  },
  eyebrow: {
    fontFamily: FONTS.body,
    fontSize: 10,
    letterSpacing: "0.4em",
    textTransform: "uppercase",
    fontWeight: 500,
  },
  body: {
    fontFamily: FONTS.body,
    fontSize: 15,
    lineHeight: 1.65,
  },
  caption: {
    fontFamily: FONTS.body,
    fontSize: 12,
    lineHeight: 1.55,
  },
};

// ---------- GLOBAL KEYFRAMES ----------
// Injected once per page (via a <style> tag) so animations are
// GPU-accelerated (transform + opacity only, no width/height thrash).
// The grain overlay is a base64 SVG noise texture — tiny (<1KB), never
// hits the network.
export const KEYFRAMES = `
@keyframes kbKenBurns {
  0%   { transform: scale(1.05) translate(0%, 0%); }
  100% { transform: scale(1.20) translate(-1.5%, -2%); }
}
@keyframes kbFadeUp {
  from { opacity: 0; transform: translateY(14px); }
  to   { opacity: 1; transform: translateY(0); }
}
@keyframes kbFadeIn {
  from { opacity: 0; }
  to   { opacity: 1; }
}
@keyframes kbGlowPulse {
  0%,100% { box-shadow: ${SHADOWS.glowTan}; }
  50%     { box-shadow: ${SHADOWS.glowTanBig}; }
}
@keyframes kbGrain {
  0%,100% { transform: translate(0, 0); }
  10% { transform: translate(-2%, -3%); }
  20% { transform: translate(-4%, 2%); }
  30% { transform: translate(1%, -4%); }
  40% { transform: translate(-1%, 3%); }
  50% { transform: translate(-3%, 1%); }
  60% { transform: translate(3%, 0%); }
  70% { transform: translate(0%, 2%); }
  80% { transform: translate(-3%, -1%); }
  90% { transform: translate(2%, 3%); }
}
.kb-ken   { animation: kbKenBurns ${DUR.kenBurns}s ${EASE.gentle} infinite alternate; will-change: transform; }
.kb-fade  { animation: kbFadeIn ${DUR.hero}ms ${EASE.hero} both; }
.kb-up    { animation: kbFadeUp ${DUR.hero}ms ${EASE.hero} both; will-change: transform, opacity; }
.kb-glow-hover { transition: box-shadow ${DUR.hover}ms ${EASE.gentle}, transform ${DUR.hover}ms ${EASE.gentle}; }
.kb-glow-hover:hover { animation: kbGlowPulse 3s ${EASE.gentle} infinite; }
.kb-card-hover { transition: transform ${DUR.hover}ms ${EASE.gentle}, box-shadow ${DUR.hover}ms ${EASE.gentle}; will-change: transform; }
.kb-card-hover:hover { transform: translateY(-2px); box-shadow: ${SHADOWS.cardHover}; }
.kb-grain {
  position: absolute; inset: -10%; pointer-events: none; opacity: 0.10; mix-blend-mode: overlay;
  background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='240' height='240'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 0.3 0 0 0 0 0.25 0 0 0 0 0.18 0 0 0 0.6 0'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>");
  background-size: 240px 240px;
  animation: kbGrain 8s steps(10) infinite;
}
.kb-vignette::after {
  content: ''; position: absolute; inset: 0; pointer-events: none;
  background:
    radial-gradient(ellipse at 50% 45%, transparent 55%, rgba(59,47,36,0.28) 100%),
    linear-gradient(180deg, rgba(59,47,36,0.10) 0%, rgba(59,47,36,0.05) 40%, rgba(59,47,36,0.45) 80%, rgba(59,47,36,0.72) 100%);
}
`;
